import { useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';

interface PlaidLinkOpenerProps {
  /** The Link token from the backend. Plaid Link opens against this. */
  token: string;
  /** Fires with Link's `public_token` once the treasurer finishes linking. */
  onSuccess: (publicToken: string) => void;
  /** Fires when the treasurer closes Link without finishing (or Link errors out). */
  onExit: () => void;
  /**
   * Set ONLY on the OAuth return remount (ADR-070): the current URL carrying
   * Plaid's `oauth_state_id`. The SDK reads it to RESUME the in-flight Link session
   * an OAuth bank (Chase, BofA, …) redirected back from, rather than start a new
   * one. Undefined on a normal fresh open (credential-based banks never leave the
   * page, so they don't need it).
   */
  receivedRedirectUri?: string;
}

/**
 * Thin wrapper over `react-plaid-link`'s `usePlaidLink` (ADR-070, #562). It has
 * no UI of its own — Plaid Link is a hosted overlay — so this renders null and
 * simply auto-opens Link as soon as the SDK reports `ready`. The parent mounts
 * it only once it holds a Link token, so the hook always has a real token.
 *
 * A `public_token` can be null in some Link products; we never exchange nothing,
 * so a null token is ignored (the treasurer can re-open Link).
 */
export function PlaidLinkOpener({
  token,
  onSuccess,
  onExit,
  receivedRedirectUri,
}: PlaidLinkOpenerProps) {
  const { open, ready } = usePlaidLink({
    token,
    receivedRedirectUri,
    onSuccess: (publicToken) => {
      if (publicToken) onSuccess(publicToken);
    },
    onExit: () => onExit(),
  });

  useEffect(() => {
    if (ready) open();
  }, [ready, open]);

  return null;
}
